import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import MoveToInboxOutlinedIcon from '@mui/icons-material/MoveToInboxOutlined';
import { DataTable, DataTableColumn, EmptyState, PageHeader } from '@routes-plan/ui';
import StandardModal, { useModalLoading } from '../../../components/modales/StandardModal';
import StockReceiptNew from './StockReceiptNew';
import PermissionButton from '../../../components/common/PermissionButton';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
import { nowOperationalDateString } from '../../../utils/operationalDateTime';
import DateRangeOfficeFilter, { RangeFilter, filterParams } from '../shared/DateRangeOfficeFilter';
import { INVENTORY_ROUTES, OfficeOption, ShowAlerts, apiErrorMessage, fetchOffices, formatDate, formatMoney, locationLabel } from '../shared/inventoryApi';
import { ActionIcon, PageBody, useInventoryAlert } from '../shared/ui';
import { useT } from '../../../i18n';
import { setPageTitle } from '../../../utils/pageTitle';
import { ScreenTourButton } from '../../../components/help/ScreenHelp';

const monthStart = () => `${nowOperationalDateString().slice(0, 8)}01`;

type Receipt = {
  id: number;
  date: string;
  office?: { id: number; name: string } | null;
  route?: { id: number; name: string } | null;
  invoice?: string | null;
  provider?: string | null;
  totalCost?: number | string | null;
  observation?: string | null;
  user?: { username?: string } | null;
  items?: ReceiptItem[];
};
type ReceiptItem = {
  id: number;
  product?: { title?: string } | null;
  quantity: number;
  unitCost: number | string;
  lotCode?: string | null;
  expirationDate?: string | null;
};

/** Datos de una entrada en dos columnas: etiqueta en gris y valor. */
const Fact: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography variant="caption" color="text.secondary" component="div">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
      {value || '—'}
    </Typography>
  </Box>
);

const StockReceiptList: React.FC<ShowAlerts> = () => {
  const t = useT();
  const showAlert = useInventoryAlert();
  const { canCreate, canRead } = useResourcePermissions(INVENTORY_ROUTES.incomes);
  const [offices, setOffices] = useState<OfficeOption[]>([]);
  const [filter, setFilter] = useState<RangeFilter>({ startDate: monthStart(), endDate: nowOperationalDateString(), officeId: '' });
  const [rows, setRows] = useState<Receipt[]>([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState<Receipt | null>(null);
  // Detalle y Registrar abren en el modal estándar (con su propio Cargando).
  const [detailId, setDetailId] = useState<number | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const detailLoading = useModalLoading();
  const addLoading = useModalLoading();
  setPageTitle('inventory.receipts.pageTitle');

  const search = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('stock-receipts', { params: filterParams(filter) });
      setRows(Array.isArray(data?.data) ? data.data : []);
    } catch (error) {
      showAlert(apiErrorMessage(error, t('inventory.receipts.loadError')), 'error');
    } finally {
      setLoading(false);
    }
  }, [filter, showAlert, t]);

  useEffect(() => {
    fetchOffices().then(setOffices).catch(() => setOffices([]));
  }, []);
  useEffect(() => {
    if (canRead) search();
    else setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canRead]);

  const openDetail = async (id: number) => {
    setDetail(null);
    setDetailId(id);
    detailLoading.showLoading();
    try {
      const { data } = await axios.get(`stock-receipts/${id}`);
      setDetail(data);
    } catch (error) {
      showAlert(apiErrorMessage(error, t('inventory.receipts.detailError')), 'error');
      setDetailId(null);
    } finally {
      detailLoading.hideLoading();
    }
  };
  const closeDetail = () => {
    setDetailId(null);
    setDetail(null);
  };

  const columns: DataTableColumn<Receipt>[] = [
    { id: 'id', header: '#', value: (r) => r.id, sortable: true },
    { id: 'date', header: t('common.date'), value: (r) => formatDate(r.date), sortable: true },
    { id: 'location', header: t('inventory.common.destination'), value: (r) => locationLabel(r), sortable: true },
    { id: 'invoice', header: t('inventory.common.invoice'), value: (r) => r.invoice ?? '', hideBelow: 'md' },
    { id: 'provider', header: t('inventory.common.supplier'), value: (r) => r.provider ?? '', sortable: true, hideBelow: 'sm' },
    { id: 'total', header: t('inventory.common.totalCost'), align: 'right', sortable: true, value: (r) => Number(r.totalCost ?? 0), render: (r) => formatMoney(r.totalCost) },
    { id: 'user', header: t('common.user'), value: (r) => r.user?.username ?? '', hideBelow: 'md' },
  ];

  const itemColumns: DataTableColumn<ReceiptItem>[] = [
    { id: 'product', header: t('inventory.common.product'), value: (i) => i.product?.title ?? '' },
    { id: 'quantity', header: t('inventory.common.quantity'), align: 'right', value: (i) => Number(i.quantity) },
    { id: 'unitCost', header: t('inventory.common.unitCostShort'), align: 'right', value: (i) => Number(i.unitCost), render: (i) => formatMoney(i.unitCost) },
    {
      id: 'subtotal',
      header: t('inventory.common.subtotal'),
      align: 'right',
      value: (i) => Number(i.unitCost) * Number(i.quantity),
      render: (i) => formatMoney(Number(i.unitCost) * Number(i.quantity)),
    },
    { id: 'lot', header: t('inventory.common.batch'), value: (i) => i.lotCode || '', hideBelow: 'sm' },
    { id: 'expires', header: t('inventory.common.expires'), value: (i) => formatDate(i.expirationDate), hideBelow: 'sm' },
  ];

  const createButton = (
    <PermissionButton
      allowed={canCreate}
      deniedTooltip={t('inventory.receipts.noPermissionCreate')}
      variant="contained"
      startIcon={<AddOutlinedIcon />}
      data-tour="receipts-new"
      onClick={() => setAddOpen(true)}
    >
      {t('inventory.receipts.createShort')}
    </PermissionButton>
  );

  const detailTotal = detail
    ? detail.totalCost ?? (detail.items || []).reduce((acc, i) => acc + Number(i.unitCost) * Number(i.quantity), 0)
    : 0;

  return (
    <PageBody>
      <PageHeader title={t('inventory.receipts.title')} description={t('inventory.receipts.description')} actions={createButton} helpAction={<ScreenTourButton />} />
      <Card>
        <CardContent>
          <DateRangeOfficeFilter offices={offices} value={filter} onChange={setFilter} onSearch={search} loading={loading} tourPrefix="receipts" />
        </CardContent>
      </Card>
      <Card>
        <CardContent>
          <DataTable<Receipt>
            rows={rows}
            columns={columns}
            getRowId={(r) => r.id}
            loading={loading}
            ariaLabel={t('inventory.receipts.title')}
            tourId="receipts"
            searchPlaceholder={t('inventory.receipts.search')}
            exportFileName={t('inventory.receipts.exportFile')}
            initialSort={{ columnId: 'id', direction: 'desc' }}
            onRowClick={canRead ? (r) => openDetail(r.id) : undefined}
            rowActions={(r) => (
              <ActionIcon
                label={t('inventory.common.viewDetail')}
                icon={<VisibilityOutlinedIcon fontSize="small" />}
                allowed={canRead}
                deniedTooltip={t('inventory.common.noPermissionRead')}
                onClick={() => openDetail(r.id)}
              />
            )}
            emptyState={
              <EmptyState
                icon={<MoveToInboxOutlinedIcon fontSize="inherit" />}
                title={t('inventory.receipts.empty')}
                description={t('inventory.receipts.emptyHint')}
                action={canCreate ? createButton : undefined}
              />
            }
          />
        </CardContent>
      </Card>
      <StandardModal
        open={detailId !== null}
        onClose={closeDetail}
        title={t('inventory.receipts.detailTitle', { id: detailId ?? '' })}
        maxWidth={900}
        loading={detailLoading.loading}
        actions={
          <>
            <span />
            <Button onClick={closeDetail} color="inherit" variant="outlined">
              {t('common.close')}
            </Button>
          </>
        }
      >
        {detail && (
          <Box sx={{ display: 'grid', gap: 2 }}>
            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'repeat(auto-fit, minmax(min(160px, 100%), 1fr))' }}>
              <Fact label={t('common.date')} value={formatDate(detail.date)} />
              <Fact label={t('inventory.common.destination')} value={locationLabel(detail)} />
              <Fact label={t('inventory.common.invoice')} value={detail.invoice} />
              <Fact label={t('inventory.common.supplier')} value={detail.provider} />
            </Box>
            <DataTable<ReceiptItem>
              rows={detail.items || []}
              columns={itemColumns}
              getRowId={(i) => i.id}
              searchable={false}
              maxHeight="none"
              ariaLabel={t('inventory.common.products')}
              exportFileName={t('inventory.receipts.detailExportFile', { id: detailId ?? '' })}
            />
            <Typography variant="subtitle1" sx={{ textAlign: 'right', fontWeight: 600 }}>
              {t('inventory.common.totalCostValue', { value: formatMoney(detailTotal) })}
            </Typography>
            {detail.observation && (
              <Typography variant="body2" color="text.secondary">
                {t('inventory.common.observationValue', { value: detail.observation })}
              </Typography>
            )}
          </Box>
        )}
      </StandardModal>
      <StandardModal open={addOpen} onClose={() => setAddOpen(false)} title={t('inventory.receipts.create')} maxWidth={1000} loading={addLoading.loading} composed>
        {addOpen && (
          <StockReceiptNew
            variant="modal"
            onClose={() => setAddOpen(false)}
            onSaved={() => {
              setAddOpen(false);
              search();
            }}
            showAlert={showAlert}
            showLoading={addLoading.showLoading}
            hideLoading={addLoading.hideLoading}
          />
        )}
      </StandardModal>
    </PageBody>
  );
};

export default StockReceiptList;
